import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { isAxiosError } from 'axios';
import api from '../lib/api';
import type { User, LoginRequest } from '../types';
import { logger } from '@/utils/logger';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User) => void;
  clearUser: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (credentials: LoginRequest) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/auth/login', credentials);
          const { user } = response.data;

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          // super_admin verwaltet RentalHub nicht mehr selbst (siehe Memory
          // project_superadmin_portal_migration) — /superadmin gibt es hier
          // nicht mehr, geht direkt ins Portal.
          if (user?.role === 'super_admin') {
            window.location.href = 'https://werkstatt-connect.de/admin';
          }
        } catch (error) {
          const errorMessage =
            (isAxiosError(error) ? error.response?.data?.detail : undefined) || 'Anmeldung fehlgeschlagen';
          set({
            error: errorMessage,
            isLoading: false,
            isAuthenticated: false,
            user: null,
          });
          throw error;
        }
      },

      logout: async () => {
        try {
          await api.post('/auth/logout');
        } catch (error) {
          logger.error('Logout error:', error);
        } finally {
          set({
            user: null,
            isAuthenticated: false,
            error: null,
          });
        }
      },

      refreshUser: async () => {
        try {
          const response = await api.get('/auth/me');
          set({
            user: response.data,
            isAuthenticated: true,
            error: null,
          });
        } catch {
          set({
            user: null,
            isAuthenticated: false,
          });
        }
      },

      clearError: () => set({ error: null }),

      // Von useAuthUser() (react-query) aus synchron gehalten — die Query ist
      // seit der Session-System-Angleichung die einzige autoritative Quelle,
      // ob die Sitzung noch gueltig ist; dieser Store bleibt nur noch fuer
      // Komponenten relevant, die synchron auf user/isAuthenticated zugreifen
      // wollen.
      setUser: (user: User) => set({ user, isAuthenticated: true, error: null }),
      clearUser: () => set({ user: null, isAuthenticated: false }),
    }),
    {
      name: 'rentalhub-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
